import { cnpj, cpf } from 'cpf-cnpj-validator';
import { isValidBrazilianUf } from '../Constants/brazilianStates';
import { CHECKOUT_FIELD_LIMITS } from '../Constants/checkoutFieldLimits';
import { PaymentMethod } from '../Types';
import { CreditCardSubmission, PagadorCartao } from '../Services/CreditCardService';
import {
  detectCardBrand,
  getMaxParcelas,
  luhnCheck,
  validateCardExpiry,
} from './cardBrand';

export interface CardFormState {
  cardNumber: string;
  cardHolderName: string;
  cardExpiry: string;
  cardCvv: string;
  installments: number;
  pagadorCpfCnpj: string;
  pagadorNome: string;
  emailContato: string;
  holderCep: string;
  holderAddress: string;
  holderNumber: string;
  holderNeighborhood: string;
  holderState: string;
  holderCity: string;
  holderPhone: string;
}

export function validatePixPagadorForm(
  pagadorCpfCnpj: string,
  pagadorNome: string,
  emailContato: string
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  if (pagadorCpfCnpj) {
    const cleaned = pagadorCpfCnpj.replace(/[^\d]/g, '');
    if (!cpf.isValid(cleaned) && !cnpj.isValid(cleaned)) {
      newErrors.pagadorCpfCnpj = 'CPF ou CNPJ inválido.';
    }
  }

  if (pagadorNome) {
    if (/\d/.test(pagadorNome)) {
      newErrors.pagadorNome = 'O nome não pode conter números.';
    } else if (pagadorNome.trim().split(/\s+/).length < 2) {
      newErrors.pagadorNome = 'Por favor, digite o nome completo.';
    }
  }

  if (emailContato && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailContato)) {
    newErrors.emailContato = 'Formato de e-mail inválido.';
  }

  return newErrors;
}

function maxLengthError(value: string, max: number, label: string): string | null {
  if (value.length > max) return `${label} deve ter no máximo ${max} caracteres.`;
  return null;
}

export function validateCreditCardCheckoutForm(
  form: CardFormState,
  method: PaymentMethod,
  options?: {
    /**
     * Fluxo hosted: os dados do cartão são digitados na página do banco,
     * então os campos do cartão não são validados (nem exibidos) aqui.
     */
    skipCardFields?: boolean;
  }
): Record<string, string> {
  const newErrors: Record<string, string> = {};
  const maxParcelas = getMaxParcelas(method);
  const skipCardFields = options?.skipCardFields === true;

  if (!skipCardFields) {
    const cardBrand = detectCardBrand(form.cardNumber);
    const digits = form.cardNumber.replace(/\D/g, '');

    if (!digits) {
      newErrors.cardNumber = 'Informe o número do cartão.';
    } else if (!cardBrand) {
      newErrors.cardNumber = 'Bandeira do cartão não identificada.';
    } else if (!cardBrand.lengths.includes(digits.length)) {
      newErrors.cardNumber = `Número inválido para ${cardBrand.niceType}.`;
    } else if (!luhnCheck(digits)) {
      newErrors.cardNumber = 'Número do cartão inválido.';
    }

    if (!form.cardHolderName.trim()) {
      newErrors.cardHolderName = 'Informe o nome impresso no cartão.';
    } else if (/\d/.test(form.cardHolderName)) {
      newErrors.cardHolderName = 'O nome não pode conter números.';
    } else {
      const err = maxLengthError(form.cardHolderName, CHECKOUT_FIELD_LIMITS.cardHolderName, 'Nome impresso');
      if (err) newErrors.cardHolderName = err;
    }

    if (!form.cardExpiry.trim()) {
      newErrors.cardExpiry = 'Informe a validade do cartão.';
    } else if (!validateCardExpiry(form.cardExpiry)) {
      newErrors.cardExpiry = 'Validade inválida ou expirada.';
    }

    const cvvDigits = form.cardCvv.replace(/\D/g, '');
    const expectedCvvLen = cardBrand?.code?.size ?? 3;
    if (!cvvDigits) {
      newErrors.cardCvv = 'Informe o código de segurança.';
    } else if (cvvDigits.length !== expectedCvvLen) {
      newErrors.cardCvv = `O CVV deve ter ${expectedCvvLen} dígitos.`;
    }
  }

  if (!form.installments || form.installments < 1 || form.installments > maxParcelas) {
    newErrors.installments = `Selecione de 1 a ${maxParcelas} parcelas.`;
  }

  const cpfDigits = form.pagadorCpfCnpj.replace(/\D/g, '');
  if (!cpfDigits) {
    newErrors.pagadorCpfCnpj = 'CPF é obrigatório.';
  } else if (!cpf.isValid(cpfDigits)) {
    newErrors.pagadorCpfCnpj = 'CPF inválido.';
  }

  if (!form.pagadorNome.trim()) {
    newErrors.pagadorNome = 'Nome completo é obrigatório.';
  } else if (/\d/.test(form.pagadorNome)) {
    newErrors.pagadorNome = 'O nome não pode conter números.';
  } else if (form.pagadorNome.trim().split(/\s+/).length < 2) {
    newErrors.pagadorNome = 'Informe o nome completo.';
  } else {
    const err = maxLengthError(form.pagadorNome, CHECKOUT_FIELD_LIMITS.pagadorNome, 'Nome completo');
    if (err) newErrors.pagadorNome = err;
  }

  const cepDigits = form.holderCep.replace(/\D/g, '');
  if (!cepDigits) {
    newErrors.holderCep = 'CEP é obrigatório.';
  } else if (cepDigits.length !== 8) {
    newErrors.holderCep = 'CEP inválido.';
  }

  if (!form.holderAddress.trim()) {
    newErrors.holderAddress = 'Endereço é obrigatório.';
  } else {
    const err = maxLengthError(form.holderAddress, CHECKOUT_FIELD_LIMITS.holderAddress, 'Endereço');
    if (err) newErrors.holderAddress = err;
  }
  if (!form.holderNumber.trim()) {
    newErrors.holderNumber = 'Número é obrigatório.';
  } else {
    const err = maxLengthError(form.holderNumber, CHECKOUT_FIELD_LIMITS.holderNumber, 'Número');
    if (err) newErrors.holderNumber = err;
  }
  if (!form.holderNeighborhood.trim()) {
    newErrors.holderNeighborhood = 'Bairro é obrigatório.';
  } else {
    const err = maxLengthError(form.holderNeighborhood, CHECKOUT_FIELD_LIMITS.holderNeighborhood, 'Bairro');
    if (err) newErrors.holderNeighborhood = err;
  }
  if (!form.holderState.trim()) {
    newErrors.holderState = 'Estado é obrigatório.';
  } else if (!isValidBrazilianUf(form.holderState)) {
    newErrors.holderState = 'Selecione um estado válido.';
  }
  if (!form.holderCity.trim()) {
    newErrors.holderCity = 'Município é obrigatório.';
  } else {
    const err = maxLengthError(form.holderCity, CHECKOUT_FIELD_LIMITS.holderCity, 'Município');
    if (err) newErrors.holderCity = err;
  }

  const phoneDigits = form.holderPhone.replace(/\D/g, '');
  if (!phoneDigits) {
    newErrors.holderPhone = 'Celular é obrigatório.';
  } else if (phoneDigits.length < 10) {
    newErrors.holderPhone = 'Celular inválido.';
  }

  if (!form.emailContato.trim()) {
    newErrors.emailContato = 'E-mail é obrigatório.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailContato)) {
    newErrors.emailContato = 'E-mail inválido.';
  } else {
    const err = maxLengthError(form.emailContato, CHECKOUT_FIELD_LIMITS.emailContato, 'E-mail');
    if (err) newErrors.emailContato = err;
  }

  return newErrors;
}

/**
 * Bandeira no formato esperado pelo SDK do C6 (ex.: VISA, MASTERCARD, ELO),
 * a partir do type do `credit-card-type` usado no `detectCardBrand`.
 */
const C6_BRAND_MAP: Record<string, string> = {
  visa: 'VISA',
  mastercard: 'MASTERCARD',
  'american-express': 'AMEX',
  'diners-club': 'DINERS',
  discover: 'DISCOVER',
  elo: 'ELO',
  hipercard: 'HIPERCARD',
  hiper: 'HIPER',
  jcb: 'JCB',
};

function toC6Brand(brandType: string | undefined): string {
  if (!brandType) return 'UNKNOWN';
  return C6_BRAND_MAP[brandType] ?? brandType.toUpperCase().replace(/-/g, '_');
}

/**
 * Monta o objeto `pagador` do contrato de cartão a partir do formulário do
 * portador — usado tanto no fluxo transparente (authorize) quanto no hosted.
 */
export function buildPagadorCartao(form: CardFormState): PagadorCartao {
  return {
    nome: form.pagadorNome.trim(),
    cpfCnpj: form.pagadorCpfCnpj.replace(/\D/g, ''),
    email: form.emailContato.trim(),
    telefone: form.holderPhone.replace(/\D/g, ''),
    endereco: {
      cep: form.holderCep.replace(/\D/g, ''),
      logradouro: form.holderAddress.trim(),
      numero: form.holderNumber.trim(),
      bairro: form.holderNeighborhood.trim(),
      cidade: form.holderCity.trim(),
      uf: form.holderState.trim().toUpperCase(),
    },
  };
}

/**
 * Monta a entrada do fluxo de cartão (checkout transparente C6).
 * Os dados abertos do cartão (`card`) são usados SOMENTE no browser para gerar
 * o card hash via C6PaySDK — nunca são enviados para a API.
 */
export function buildCreditCardSubmission(
  linkPagamentoId: string,
  form: CardFormState
): CreditCardSubmission {
  const cardBrand = detectCardBrand(form.cardNumber);
  const [expirationMonth = '', expirationYearShort = ''] = form.cardExpiry.split('/');
  return {
    linkPagamentoId,
    qtdParcelas: form.installments,
    card: {
      number: form.cardNumber.replace(/\D/g, ''),
      holderName: form.cardHolderName.trim(),
      expirationMonth,
      expirationYear: `20${expirationYearShort}`,
      cvv: form.cardCvv.replace(/\D/g, ''),
      brand: toC6Brand(cardBrand?.type),
    },
    pagador: buildPagadorCartao(form),
  };
}
